import { Component, OnDestroy, OnInit } from '@angular/core';
import { finalize, Subscription } from 'rxjs';
import { emptyOrderEntityFilters, Order, OrderEntityFilters, OrderItem } from '../../orders.models';
import { OrdersService } from '../../orders.service';
import { PaginatedResponse } from '../../../common/models/common.model';
import { Router } from '@angular/router';
import { SnackbarService } from '../../../services/snackbar.service';
import { buildFilterQuery, SnackbarAlert, SnackbarAlertStatus } from '../../../utils';

@Component({
  selector: 'app-pending-orders',
  standalone: false,
  templateUrl: './pending-orders.component.html',
  styleUrl: './pending-orders.component.scss',
})
export class PendingOrdersComponent implements OnInit, OnDestroy {
  protected orders: Order[] = [];
  protected isDataLoading = false;
  protected isExporting = false;
  protected currentPage = 1;
  protected totalCount = 0;
  protected hasNextPage = false;
  protected hasPreviousPage = false;
  protected readonly pageSize = 10;
  protected filters: OrderEntityFilters = emptyOrderEntityFilters();

  private subscription!: Subscription;

  constructor(
    private ordersService: OrdersService,
    private router: Router,
    private snackbarService: SnackbarService
  ) {}

  ngOnInit(): void {
    this.loadOrders();
  }

  protected get hasActiveFilters(): boolean {
    return Object.values(this.filters).some(Boolean);
  }

  protected onFiltersChange(filters: OrderEntityFilters): void {
    this.filters = filters;
    // A newly filtered result set may not have the page the user is currently on.
    this.currentPage = 1;
    this.loadOrders();
  }

  protected loadOrders(): void {
    this.isDataLoading = true;

    this.subscription = this.ordersService
      .get_orders({
        page: this.currentPage,
        is_fully_shipped: false,
        is_fully_delivered: false,
        is_order_cancelled: false,
        ...this.activeFilterParams,
      })
      .pipe(finalize(() => (this.isDataLoading = false)))
      .subscribe({
        next: (res: PaginatedResponse<Order>) => {
          this.orders = res.results;
          this.totalCount = res.count;
          this.hasNextPage = !!res.next;
          this.hasPreviousPage = !!res.previous;
        },
      });
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this.loadOrders();
  }

  /** Empty filters are dropped so the query string never carries `product=` or `product=null`. */
  private get activeFilterParams(): Record<string, string> {
    return buildFilterQuery(this.filters);
  }

  protected exportPending(): void {
    this.isExporting = true;

    // Same filters as the list, so the file matches what the user is looking at.
    this.ordersService
      .exportPending(this.activeFilterParams)
      .pipe(finalize(() => (this.isExporting = false)))
      .subscribe({
        next: response => {
          const blob = response.body!;
          const disposition = response.headers.get('Content-Disposition') ?? '';
          const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          const filename =
            match?.[1]?.replace(/['"]/g, '') ||
            `Pending_Orders_Report_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.xlsx`;

          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          URL.revokeObjectURL(url);
        },
        error: () => {
          this.snackbarService.show(SnackbarAlert.SOMETHING_WENT_WRONG, SnackbarAlertStatus.ERROR);
        },
      });
  }

  protected viewOrder(orderId: string): void {
    void this.router.navigate(['/orders', orderId]);
  }

  /** "5 Kg × 4 · Carton": the packaging line shown under every product name. */
  protected getPackagingLabel(item: OrderItem): string {
    const packaging = item.product_packaging;
    const parts = [`${packaging.packing_size} ${packaging.unit?.short_name ?? ''}`.trim()];

    if (packaging.nos) {
      parts.push(`× ${packaging.nos}`);
    }
    if (packaging.packaging_type) {
      parts.push(`· ${packaging.packaging_type.name}`);
    }

    return parts.join(' ');
  }

  protected getProductName(item: OrderItem): string {
    const product = item.product_packaging.product;
    return typeof product === 'string' ? product : (product as any).name;
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
